'use client';

import { useTranslations } from 'next-intl';
import { KrModal } from '@/components/ui/agartha/KrModal';

/**
 * Modal gry w ramce „Kronika" (Agartha_UI_Kit.afdesign, artboardy 19–19J):
 * pas nagłówka z medalionem typu, tytułem, podtytułem i pigułkami; złote narożniki;
 * zamknięcie na rogu; pas stopki z podpowiedzią i akcjami; okno na środku ekranu.
 * Wewnątrz działa skórka Agartha — stare `FantasyButton` renderują się jako pigułki.
 *
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {Function} [props.onMinimize]
 * @param {React.ReactNode} [props.title]
 * @param {React.ReactNode} [props.subtitle] — linia pod tytułem (kontekst: źródło, runda, zasada)
 * @param {React.ComponentType} [props.icon] — ikona lucide w medalionie nagłówka
 * @param {string} [props.iconSrc] — albo obraz w medalionie
 * @param {string} [props.tint] — kolor poświaty medalionu
 * @param {React.ReactNode} [props.headerRight] — pigułki po prawej stronie nagłówka (koszt, licznik, zakładki)
 * @param {React.ReactNode} [props.hint] — podpowiedź w lewej części stopki
 * @param {React.ReactNode} [props.footer] — akcje stopki (nie przewijają się z treścią)
 * @param {'sm'|'md'|'lg'|'xl'|'full'} [props.size] — szerokość okna (domyślnie z klas max-w-* albo 'md')
 * @param {boolean} [props.fullscreen]
 */
const SIZE_BY_MAXW = [
  [/max-w-(none|full|screen|\[9\dvw\])/, 'full'],
  [/max-w-(5xl|6xl|7xl)/, 'xl'],
  [/max-w-(3xl|4xl|\[4\drem\]|\[5\drem\])/, 'lg'],
  [/max-w-(lg|xl|2xl)/, 'md'],
  [/max-w-(xs|sm|md)/, 'sm'],
];

function splitSize(className = '') {
  let size = null;
  const rest = String(className)
    .split(/\s+/)
    .filter((c) => {
      const bare = c.replace(/^(!|sm:|md:|lg:)+/, '');
      if (!/^!?max-w-/.test(bare)) return true;
      // tylko podstawowy wariant (bez prefiksu breakpointu) wyznacza rozmiar okna
      if (!size && !/^(sm|md|lg):/.test(c)) for (const [re, s] of SIZE_BY_MAXW) if (re.test(bare)) { size = s; break; }
      return false;
    })
    .join(' ');
  return { size, rest };
}

export function Modal({
  isOpen, onClose, onMinimize, children, title, subtitle, icon, iconSrc, tint, headerRight, hint,
  showCloseButton = true, closeOnBackdrop = true, className, zIndex = 9999, maxHeight = '88vh',
  footer, footerAlign, fullscreen = false, size, bodyClassName, closeHighlight,
  // eslint-disable-next-line no-unused-vars -- dawny tryb dokowania; ramka „Kronika" zawsze stoi na środku
  centered,
}) {
  const commonT = useTranslations('Common');
  const { size: fromClass, rest } = splitSize(className);
  const resolved = size || fromClass || 'md';
  return (
    <KrModal
      isOpen={isOpen}
      onClose={onClose}
      onMinimize={onMinimize}
      title={title}
      subtitle={subtitle}
      icon={icon}
      iconSrc={iconSrc}
      tint={tint}
      headerRight={headerRight}
      hint={hint}
      footer={footer}
      footerAlign={footerAlign || 'end'}
      size={resolved === 'full' ? undefined : resolved}
      className={[resolved === 'full' && 'kr-modal--full', rest].filter(Boolean).join(' ')}
      bodyClassName={bodyClassName}
      zIndex={zIndex}
      maxHeight={maxHeight}
      showCloseButton={showCloseButton}
      closeOnBackdrop={closeOnBackdrop}
      closeLabel={commonT('close')}
      minimizeLabel={commonT('minimize')}
      fullscreen={fullscreen}
      closeHighlight={closeHighlight}
    >
      {children}
    </KrModal>
  );
}

export default Modal;
